import { memoize, shallowMemoize } from '@sail/utils';
import addCarryOverIdentifiers from '../../internal/addCarryOverIdentifiers';
import tokenizePath from '../../internal/compiler/tokenizePath';
import replaceGlobal from '../../internal/replaceGlobal';
import stripTrailingSlash from '../../internal/stripTrailingSlash';
// Helper regular expressions to build the final one.
const rParam = /:[^:/(){}]+(?:\([^()]+\))?[?!]?/;
const rStatic = /[^/{}]+/;
const rPart = new RegExp(`/(?:${rParam.source}|${rStatic.source})`);
const rFull = new RegExp(`\\{(?:${rPart.source})+\\}[?!]?|${rPart.source}`, 'g');
const parsePath = memoize((path)=>{
    return replaceGlobal(stripTrailingSlash(path));
});
function buildTree(result, pieces, name) {
    const leafNodes = [];
    function traverse(index, currentTree) {
        if (!pieces || index >= pieces.length) {
            leafNodes.push(currentTree);
            return;
        }
        const piece = pieces[index];
        const isOptional = piece.endsWith('?') || piece.endsWith('!');
        const key = isOptional ? piece.slice(0, -1) : piece;
        const isGroup = key.startsWith('{') && key.endsWith('}');
        // If an item is a group we treat the group as a smaller pattern, thus
        // calling build tree. Then, over each leaf node, we deep traverse the
        // children. Otherwise, we just take the parameter and add it to the current
        // tree.
        if (isGroup) {
            const innerPieces = key.slice(1, -1).match(rFull);
            const innerLeafNodes = buildTree(currentTree, innerPieces, name);
            innerLeafNodes.forEach((leafNode)=>{
                traverse(index + 1, leafNode);
            });
        } else {
            const fragment = key.substr(1);
            const slot = fragment.startsWith(':') ? 'p' : 's'; // "param" or "static".
            let id = fragment.startsWith(':') ? fragment.slice(1) : fragment.toLowerCase();
            // adding the RouteName to the route to avoid collisions on optional properties
            if (slot === 'p' && isOptional) {
                id = `${name}${id}`;
            }
            currentTree[slot] = currentTree[slot] || {};
            // TODO: using a variable just to make TS happy, this can be removed once we hit TS 5.5. See https://devblogs.microsoft.com/typescript/announcing-typescript-5-5/#control-flow-narrowing-for-constant-indexed-accesses2
            const currentSlot = currentTree[slot];
            if (currentSlot) {
                currentSlot[id] = currentSlot[id] || {};
                traverse(index + 1, currentSlot[id]);
            }
        }
        // If data was optional we also traverse the next item onto the current
        // item, effectively adding a branch skipping the extra item.
        if (isOptional) {
            traverse(index + 1, currentTree);
        }
    }
    traverse(0, result);
    return leafNodes;
}
export default shallowMemoize(function toTree(routes) {
    const tree = {
        l: Object.keys(routes),
        t: {}
    };
    for(const _name in routes){
        if (routes.hasOwnProperty(_name)) {
            const name = _name;
            const route = routes[name];
            const pattern = typeof route === 'string' ? parsePath(route) : parsePath(route[0]);
            const pieces = pattern.match(rFull);
            tree.t[name] = tokenizePath(addCarryOverIdentifiers(pattern));
            if (pieces) {
                const leafNodes = buildTree(tree, pieces, name);
                leafNodes.forEach((leafNode)=>{
                    if (!leafNode.n) {
                        leafNode.n = [
                            name
                        ];
                    } else {
                        leafNode.n.push(name);
                    }
                });
            } else {
                tree.n = [
                    name
                ];
            }
        }
    }
    return tree;
});

//# sourceMappingURL=toTree.js.map